import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Mail, Clock, LogIn, ChevronDown, Menu, X, ArrowRight } from "lucide-react";
import { company } from "../../mock";
import { publicApi } from "../../lib/api";
import { getIcon } from "../../lib/icons";
import GoogleRatingBadge from "../GoogleRatingBadge";
import logoLight from "../../assets/cass-logo-light.png";
import { NAV_FALLBACK } from "../../lib/navDefaults";

const CassLogo = () => (
  <Link to="/" className="flex items-center group">
    <img loading="eager" fetchPriority="high" decoding="async" src={logoLight} alt="Cloud Accounting Support Services" className="h-14 w-auto" />
  </Link>
);

const TopBar = () => (
  <div className="hidden md:block bg-navy-deep text-slate-200 text-xs">
    <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-9">
      <div className="flex items-center gap-6">
        <a href={`tel:${company.phone}`} className="flex items-center gap-1.5 hover:text-orange transition-colors">
          <Phone size={13} /> {company.phone}
        </a>
        <a href={`mailto:${company.email}`} className="flex items-center gap-1.5 hover:text-orange transition-colors">
          <Mail size={13} /> {company.email}
        </a>
        <span className="flex items-center gap-1.5">
          <Clock size={13} /> {company.hours}
        </span>
      </div>
      <div className="flex items-center gap-5">
        <GoogleRatingBadge dark className="border-r border-white/15 pr-5" />
        <a href="https://cassonline.portal.engager.app/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 font-semibold tracking-wide hover:text-orange transition-colors">
          <LogIn size={13} /> CLIENT LOGIN
        </a>
      </div>
    </div>
  </div>
);

const NavItem = ({ to, children, testId }) => {
  const location = useLocation();
  const active = location.pathname === to;
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`relative text-sm font-semibold tracking-wide py-2 transition-colors whitespace-nowrap ${
        active ? "text-orange" : "text-white hover:text-orange"
      }`}
    >
      {children}
      <span className={`absolute left-0 -bottom-0.5 h-0.5 bg-orange transition-all duration-300 ${active ? "w-full" : "w-0"}`} />
    </Link>
  );
};

// Accessible header dropdown: opens on CLICK (and keyboard Enter/Space/ArrowUp/ArrowDown) only — not hover.
// Full arrow-key navigation between items, Home/End jumps, Escape returns focus to the trigger,
// Tab/outside-click/focus-leave close. Exposes aria-haspopup/aria-expanded + role=menu.
const NavDropdown = ({ label, testId, children }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const pendingFocus = useRef(null); // 'first' | 'last' | null

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const items = () => Array.from(ref.current?.querySelectorAll('[data-dropdown-panel] a, [data-dropdown-panel] button') || []);
  const focusItem = (i) => { const l = items(); if (l.length) (l[(i + l.length) % l.length] || l[0]).focus(); };

  // Apply focus AFTER the panel is rendered visible (avoids focusing a visibility:hidden element).
  useEffect(() => {
    if (open && pendingFocus.current) {
      const where = pendingFocus.current;
      pendingFocus.current = null;
      requestAnimationFrame(() => focusItem(where === "last" ? -1 : 0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onKeyDown = (e) => {
    const list = items();
    const idx = list.indexOf(document.activeElement);
    if (e.key === "Escape") {
      if (open) { setOpen(false); triggerRef.current?.focus(); }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) { pendingFocus.current = "first"; setOpen(true); }
      else focusItem(idx + 1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) { pendingFocus.current = "last"; setOpen(true); }
      else focusItem(idx - 1);
    } else if (e.key === "Home" && open && idx >= 0) {
      e.preventDefault(); focusItem(0);
    } else if (e.key === "End" && open && idx >= 0) {
      e.preventDefault(); focusItem(-1);
    } else if (e.key === "Tab" && open) {
      setOpen(false);
    }
  };

  const onBlur = (e) => {
    if (ref.current && !ref.current.contains(e.relatedTarget)) setOpen(false);
  };

  return (
    <div ref={ref} className="relative" onBlur={onBlur} onKeyDown={onKeyDown}>
      <button
        ref={triggerRef}
        type="button"
        data-testid={testId}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 text-sm font-semibold tracking-wide text-white hover:text-orange transition-colors py-2 whitespace-nowrap"
      >
        {label} <ChevronDown size={15} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <div
        data-dropdown-panel
        role="menu"
        aria-label={label}
        className={`absolute left-1/2 -translate-x-1/2 top-full pt-3 transition-[opacity,transform] duration-200 ${
          open ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2"
        }`}
        onClick={() => setOpen(false)}
      >
        {children}
      </div>
    </div>
  );
};

// A nav item's target may be an internal route or an external URL (custom items).
const NavLinkish = ({ item, className, children, testId }) => {
  const external = /^https?:\/\//i.test(item.url || "");
  if (external) {
    return <a href={item.url} target="_blank" rel="noopener noreferrer" className={className} data-testid={testId}>{children}</a>;
  }
  return <Link to={item.url || "/"} className={className} data-testid={testId}>{children}</Link>;
};

// Standard dropdown row: plain link, optional "Free"-style badge, optional
// description (renders as the richer accent card used for Legal Cashiering).
const DropdownRow = ({ item }) => {
  const Icon = item.icon ? getIcon(item.icon) : null;
  if (item.desc) {
    return (
      <NavLinkish item={item} testId={`nav-${item.id}`} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-orange/5 transition-colors">
        {Icon && <span className="w-8 h-8 rounded-md bg-navy flex items-center justify-center text-white shrink-0"><Icon size={16} /></span>}
        <span className="leading-tight">
          <span className="block text-sm font-bold text-navy">{item.label}</span>
          <span className="block text-xs text-slate-400">{item.desc}</span>
        </span>
      </NavLinkish>
    );
  }
  return (
    <NavLinkish item={item} testId={`nav-${item.id}`} className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-slate-50 text-sm font-medium text-navy">
      {item.label}
      {item.badge && <span className="ml-2 text-[10px] font-bold uppercase tracking-wide bg-orange/10 text-orange px-1.5 py-0.5 rounded">{item.badge}</span>}
    </NavLinkish>
  );
};

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [services, setServices] = useState([]);
  const [serviceGroups, setServiceGroups] = useState([]);
  const [nav, setNav] = useState(NAV_FALLBACK);
  const [packageLive, setPackageLive] = useState(false);
  const location = useLocation();

  useEffect(() => {
    publicApi.services().then(setServices).catch(() => setServices([]));
    // Only advertise the package builder while it is fully live.
    publicApi.packagePage().then((c) => setPackageLive((c.visibility || "live") === "live")).catch(() => {});
    publicApi.serviceGroups().then(setServiceGroups).catch(() => setServiceGroups([]));
    publicApi.navigation().then((d) => { if (d?.items?.length) setNav(d.items); }).catch(() => {});
  }, []);

  // Items can be gated on something only the frontend knows (e.g. the pricing
  // journey being live) — hide those until the gate opens.
  const allowed = (it) => !(it.condition === "package_live" && !packageLive);
  const navItems = nav.filter(allowed);
  const children = (it) => (it.children || []).filter(allowed);

  // Group services into columns by their group, ordered by the admin-defined group order.
  const groupedServices = (() => {
    const groups = [...serviceGroups].sort((a, b) => (a.order || 0) - (b.order || 0));
    const cols = groups.map((g) => ({ ...g, items: services.filter((s) => s.group === g.key) }));
    const known = new Set(groups.map((g) => g.key));
    const orphans = services.filter((s) => !known.has(s.group));
    if (orphans.length) cols.push({ key: "_other", label: "More", items: orphans });
    return cols.filter((c) => c.items.length > 0);
  })();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50">
      <TopBar />
      <div className={`bg-navy transition-shadow duration-300 ${scrolled ? "shadow-lg shadow-navy/30" : ""}`}>
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-20">
          <CassLogo />

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-5 xl:gap-6">
            {navItems.map((it) => {
              if (it.type !== "group") {
                const external = /^https?:\/\//i.test(it.url || "");
                if (external) {
                  return (
                    <a key={it.id} href={it.url} target="_blank" rel="noopener noreferrer" data-testid={`nav-${it.id}`} className="text-sm font-semibold tracking-wide text-white hover:text-orange transition-colors py-2 whitespace-nowrap">{it.label}</a>
                  );
                }
                return <NavItem key={it.id} to={it.url} testId={`nav-${it.id}`}>{it.label}</NavItem>;
              }
              // The Services menu keeps its mega-panel (service columns + accent cards).
              if (it.mega === "services") {
                const cards = children(it).filter((c) => c.desc);
                const plain = children(it).filter((c) => !c.desc);
                return (
                  <NavDropdown key={it.id} label={it.label} testId={`nav-${it.id}-trigger`}>
                    <div className="bg-white rounded-xl shadow-2xl border border-slate-100 p-4" style={{ minWidth: `${Math.min(Math.max(groupedServices.length, 1), 3) * 240 + 16}px` }}>
                      <Link to="/services" data-testid="nav-all-services" className="flex items-center justify-between px-3 py-2 mb-2 rounded-lg hover:bg-orange/5 text-sm font-bold text-navy">
                        All Services <ArrowRight size={15} className="text-orange" />
                      </Link>
                      <div className="flex gap-6">
                        {groupedServices.map((col, ci) => (
                          <div key={col.key} className="min-w-[220px]">
                            <p className="px-3 pb-2 mb-1 text-xs font-bold uppercase tracking-wider text-orange border-b border-slate-100">{col.label}</p>
                            {col.items.map((s) => {
                              const Icon = getIcon(s.icon);
                              return (
                                <Link key={s.slug} to={s.external_path || `/services/${s.slug}`} className="flex items-center gap-3 px-3 h-[52px] rounded-lg hover:bg-slate-50 group/item">
                                  <span className="w-8 h-8 rounded-md bg-navy/5 flex items-center justify-center text-navy group-hover/item:bg-orange group-hover/item:text-white transition-colors shrink-0">
                                    <Icon size={16} />
                                  </span>
                                  <span className="text-sm font-medium text-navy leading-tight line-clamp-2">{s.title}</span>
                                </Link>
                              );
                            })}
                            {ci === groupedServices.length - 1 && plain.map((p) => (
                              <NavLinkish key={p.id} item={p} testId={`nav-${p.id}`} className="flex items-center gap-3 px-3 h-[52px] rounded-lg hover:bg-slate-50">
                                <span className="w-8 h-8 rounded-md bg-navy/5 flex items-center justify-center text-navy shrink-0"><ChevronDown size={16} className="-rotate-90" /></span>
                                <span className="text-sm font-medium text-navy leading-tight line-clamp-2">{p.label}</span>
                              </NavLinkish>
                            ))}
                          </div>
                        ))}
                      </div>
                      {!groupedServices.length && plain.map((p) => <DropdownRow key={p.id} item={p} />)}
                      {cards.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
                          {cards.map((c) => <DropdownRow key={c.id} item={c} />)}
                        </div>
                      )}
                    </div>
                  </NavDropdown>
                );
              }
              return (
                <NavDropdown key={it.id} label={it.label} testId={`nav-${it.id}-trigger`}>
                  <div className="w-64 bg-white rounded-xl shadow-2xl border border-slate-100 p-2">
                    {children(it).map((c) => <DropdownRow key={c.id} item={c} />)}
                  </div>
                </NavDropdown>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              className="hidden md:inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold text-sm px-6 py-3 rounded-full transition-all hover:shadow-lg hover:shadow-orange/30 hover:-translate-y-0.5"
            >
              Let's Talk <ArrowRight size={16} />
            </Link>
            <button className="lg:hidden text-white transition-transform active:scale-90" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu" aria-expanded={mobileOpen}>
              <span className={`inline-block transition-transform duration-300 ${mobileOpen ? "rotate-90" : "rotate-0"}`}>
                {mobileOpen ? <X size={26} /> : <Menu size={26} />}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu — smooth height + fade/slide, auto-closes when a link is tapped */}
      <div className={`lg:hidden bg-navy-deep overflow-hidden transition-all duration-300 ease-out ${mobileOpen ? "max-h-[90vh] overflow-y-auto" : "max-h-0"}`}>
        <div
          className={`px-6 py-4 flex flex-col gap-1 transition-[opacity,transform] duration-300 ${mobileOpen ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"}`}
          onClick={(e) => { if (e.target.closest("a")) setMobileOpen(false); }}
        >
          {navItems.map((it) => {
            if (it.type !== "group") {
              const external = /^https?:\/\//i.test(it.url || "");
              if (external) {
                return <a key={it.id} href={it.url} target="_blank" rel="noopener noreferrer" data-testid={`mobile-nav-${it.id}`} className="py-3 text-white font-semibold border-b border-white/10">{it.label}</a>;
              }
              return <Link key={it.id} to={it.url || "/"} data-testid={`mobile-nav-${it.id}`} className="py-3 text-white font-semibold border-b border-white/10">{it.label}</Link>;
            }
            return (
              <details key={it.id} className="border-b border-white/10">
                <summary className="py-3 text-white font-semibold cursor-pointer flex items-center justify-between" data-testid={`mobile-nav-${it.id}-trigger`}>{it.label} <ChevronDown size={16} /></summary>
                <div className="pb-3 pl-3 flex flex-col [&>a]:min-h-[44px] [&>a]:flex [&>a]:items-center [&>a]:gap-2">
                  {it.mega === "services" && (
                    <>
                      <Link to="/services" className="py-2 text-slate-300 text-sm font-medium">All Services</Link>
                      {services.map((s) => (
                        <Link key={s.slug} to={s.external_path || `/services/${s.slug}`} className="py-2 text-slate-300 text-sm">{s.title}</Link>
                      ))}
                    </>
                  )}
                  {children(it).map((c) => (
                    /^https?:\/\//i.test(c.url || "") ? (
                      <a key={c.id} href={c.url} target="_blank" rel="noopener noreferrer" data-testid={`mobile-nav-${c.id}`} className="py-2 text-slate-300 text-sm">{c.label}</a>
                    ) : (
                      <Link key={c.id} to={c.url || "/"} data-testid={`mobile-nav-${c.id}`} className={`py-2 text-sm flex items-center gap-2 ${c.accent ? "text-orange font-semibold" : "text-slate-300"}`}>
                        {c.label}
                        {c.badge && <span className="text-[10px] font-bold uppercase bg-orange/20 text-orange px-1.5 py-0.5 rounded">{c.badge}</span>}
                      </Link>
                    )
                  ))}
                </div>
              </details>
            );
          })}
          <Link to="/contact" className="mt-4 inline-flex items-center justify-center gap-2 bg-orange text-white font-bold text-sm px-6 py-3 rounded-full">
            Let's Talk <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </header>
  );
}
